import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, FileText, Download } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import { API } from "@/lib/api";
import CalendlyInlineBooking from "@/components/CalendlyInlineBooking";

const ContactPage = () => {
  const seoOverride = usePageSeo("contact");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry_type: "general",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, inquiry_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        inquiry_type: "general",
        message: "",
      });
    } catch (err) {
      toast.error("There was an error sending your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      content: "(305) 929-3349",
      link: "tel:+13059293349",
    },
    {
      icon: Mail,
      title: "Email",
      content: "Divinelovewellness@protonmail.com",
      link: "mailto:Divinelovewellness@protonmail.com",
    },
    {
      icon: MapPin,
      title: "Office Address",
      content: "3206 NE 2nd Ave #1105, Miami, FL 33137",
      link: "https://maps.google.com/?q=3206+NE+2nd+Ave+%231105+Miami+FL+33137",
    },
    {
      icon: MapPin,
      title: "Licensed In",
      content: "Florida, Maine, District of Columbia (DC)",
      link: null,
    },
    {
      icon: Clock,
      title: "Hours of Operation",
      content: "Monday - Saturday EST, By Appointment Only",
      link: null,
    },
  ];

  return (
    <div className="pt-20" data-testid="contact-page">
      <SEO
        title="Contact"
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        description={seoOverride?.meta_description || "Reach out to Divine Love Wellness Enterprises to begin your healing journey. Free 40-minute consultations available. Hours: Monday–Saturday EST, by appointment."}
        imageAlt={seoOverride?.image_alt}
        pathname="/contact"
      />
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-foreground leading-tight">
              Begin Your <span className="italic text-primary">Healing Journey</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              We're here to answer your questions and help you take the first step toward 
              transformative mental wellness. Reach out today to connect with Laura.
            </p>

            {/* Consultation Prep Guide */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8 max-w-xl"
              data-testid="consultation-prep-guide-card"
            >
              <a
                href="/what-to-expect-consultation.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-5 bg-white/80 hover:bg-white border border-primary/30 rounded-2xl transition-all hover:shadow-soft group"
                data-testid="download-consultation-guide-link"
              >
                <div className="p-3 rounded-full bg-primary/10 flex-shrink-0">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground flex items-center gap-2">
                    Free Consultation Prep Guide
                    <Download className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    A gentle one-page PDF on what to expect and how to prepare for your free 40-minute call.
                  </p>
                </div>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Book a Free Consultation — Calendly Inline (moved to top per user request) */}
      <section
        className="py-20 md:py-24 px-6 md:px-12 bg-white"
        data-testid="contact-calendly-section"
      >
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Ready When You Are
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground">
              Book a Free 40-Minute Consultation
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Pick a time that works for you — no forms, no back-and-forth. We&rsquo;ll meet, hear what&rsquo;s bringing you here, and see if working together feels right.
            </p>
          </motion.div>
          <CalendlyInlineBooking />
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <Card className="p-8 md:p-10 bg-white border-border/40 rounded-3xl shadow-soft">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="mx-auto w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-6">
                      <CheckCircle className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-serif text-2xl font-semibold text-foreground mb-3">
                      Thank You!
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Your message has been received. Our team will contact you within 24 hours.
                    </p>
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      variant="outline"
                      className="rounded-full"
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-5 mb-8 pb-8 border-b border-border/40" data-testid="contact-laura-intro">
                      <img
                        src="https://customer-assets.emergentagent.com/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/l4ja8zkq_IMG_4558_Facetune_17-01-2023-21-22-22.jpeg"
                        alt="Laura A. Jeanis, LCSW, LICSW — founder and licensed ketamine psychotherapist"
                        className="h-24 w-24 md:h-28 md:w-28 rounded-full object-cover object-top ring-4 ring-secondary shadow-soft flex-shrink-0"
                        loading="lazy"
                        data-testid="contact-laura-photo"
                      />
                      <div>
                        <p className="font-serif text-xl md:text-2xl font-semibold text-foreground leading-tight">
                          Laura A. Jeanis
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">LCSW, LICSW, CCTP, CEO</p>
                        <p className="font-serif italic text-primary mt-2">I look forward to hearing from you.</p>
                      </div>
                    </div>
                    <h2 className="font-serif text-2xl font-semibold text-foreground mb-2">
                      Request a Consultation
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      Fill out the form below and we'll get back to you within 24 hours.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label 
                            htmlFor="name" 
                            className="text-sm font-medium text-muted-foreground uppercase tracking-wider"
                          >
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Your full name"
                            className="h-12 rounded-xl bg-white/50 border-border/60 focus:border-primary/50"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label 
                            htmlFor="email" 
                            className="text-sm font-medium text-muted-foreground uppercase tracking-wider"
                          >
                            Email *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="your@email.com"
                            className="h-12 rounded-xl bg-white/50 border-border/60 focus:border-primary/50"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label 
                            htmlFor="phone" 
                            className="text-sm font-medium text-muted-foreground uppercase tracking-wider"
                          >
                            Phone Number
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(555) 555-5555"
                            className="h-12 rounded-xl bg-white/50 border-border/60 focus:border-primary/50"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label 
                            htmlFor="inquiry_type" 
                            className="text-sm font-medium text-muted-foreground uppercase tracking-wider"
                          >
                            Inquiry Type
                          </Label>
                          <Select
                            value={formData.inquiry_type}
                            onValueChange={handleSelectChange}
                          >
                            <SelectTrigger 
                              className="h-12 rounded-xl bg-white/50 border-border/60"
                              data-testid="contact-inquiry-select"
                            >
                              <SelectValue placeholder="Select inquiry type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General Inquiry</SelectItem>
                              <SelectItem value="consultation">Free Consultation</SelectItem>
                              <SelectItem value="depression">Depression Treatment</SelectItem>
                              <SelectItem value="anxiety">Anxiety Treatment</SelectItem>
                              <SelectItem value="trauma">Trauma Treatment</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label 
                          htmlFor="message" 
                          className="text-sm font-medium text-muted-foreground uppercase tracking-wider"
                        >
                          Your Message *
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Tell us about your situation and how we can help..."
                          rows={5}
                          className="rounded-xl bg-white/50 border-border/60 focus:border-primary/50 resize-none"
                          data-testid="contact-message-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full rounded-full py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          "Sending..."
                        ) : (
                          <>
                            Send Message
                            <Send className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-muted-foreground text-center">
                        Your information is confidential and protected under HIPAA guidelines.
                      </p>
                    </form>
                  </>
                )}
              </Card>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-6"
            >
              <h3 className="font-serif text-2xl font-semibold text-foreground">
                Get in Touch
              </h3>
              <p className="text-muted-foreground">
                Prefer to reach out directly? Contact us using the information below.
              </p>

              <div className="space-y-6 mt-8">
                {contactInfo.map((info, index) => (
                  <div key={info.title} className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-secondary flex-shrink-0">
                      <info.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{info.title}</h4>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-muted-foreground hover:text-primary transition-colors"
                          data-testid={`contact-info-${info.title.toLowerCase()}`}
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-muted-foreground">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Embedded Google Map for the Miami office */}
              <Card className="mt-8 p-0 overflow-hidden rounded-2xl border-border/40" data-testid="contact-map-card">
                <iframe
                  title="Divine Love Wellness Enterprises — 3206 NE 2nd Ave #1105, Miami, FL 33137"
                  src="https://www.google.com/maps?q=3206+NE+2nd+Ave+%231105+Miami+FL+33137&output=embed"
                  className="w-full aspect-video border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </Card>

              {/* Emergency Notice */}
              <Card className="p-6 bg-destructive/5 border-destructive/20 rounded-xl">
                <h4 className="font-semibold text-foreground mb-2">
                  Need Immediate Help?
                </h4>
                <p className="text-sm text-muted-foreground">
                  If you're experiencing a mental health emergency, please call 988 
                  (Suicide & Crisis Lifeline) or go to your nearest emergency room.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
